export const Spinner = ({ full }) => (
  <div className={`flex items-center justify-center ${full ? "min-h-[60vh]" : "py-16"}`}>
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

export const Pill = ({ children, tone = "muted" }) => {
  const tones = {
    muted: "bg-zinc-100 text-zinc-700 border-zinc-300",
    blue: "bg-blue-50 text-primary border-blue-200",
    red: "bg-red-50 text-destructive border-red-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-mono uppercase tracking-wide border ${tones[tone]}`}>
      {children}
    </span>
  );
};

export const StatBox = ({ value, label, testid }) => (
  <div className="border border-zinc-300 bg-white p-6" data-testid={testid}>
    <div className="font-heading font-900 text-3xl lg:text-4xl tracking-tight tabular-nums">{value}</div>
    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
  </div>
);

// Vibrant page themes — blue, green, purple, orange. Backgrounds are deep enough
// for white text (WCAG AA) while staying colorful instead of near-black.
export const VIBRANT_THEMES = [
  { key: "blue", bg: "#1D4ED8", accent: "#BFDBFE" },
  { key: "green", bg: "#047857", accent: "#A7F3D0" },
  { key: "purple", bg: "#6D28D9", accent: "#DDD6FE" },
  { key: "orange", bg: "#C2410C", accent: "#FED7AA" },
];
export const vibrantTheme = (seed = "") => {
  let hash = 0;
  for (const char of String(seed)) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return VIBRANT_THEMES[hash % VIBRANT_THEMES.length];
};

// Clickable category icons baked into the sub-page banner artwork (percentage-mapped).
const BANNER_HOTSPOTS = [
  { key: "air", label: "Air Travel", to: "/air", left: "4.5%", width: "6.5%" },
  { key: "rail", label: "Rail Travel", to: "/rail", left: "11.2%", width: "6.5%" },
  { key: "bus", label: "Bus & Coach", to: "/bus", left: "18.0%", width: "6.5%" },
  { key: "ground", label: "Ground Transport", to: "/ground", left: "25.5%", width: "7%" },
  { key: "rideshare", label: "Rideshare", to: "/rideshare", left: "34.5%", width: "8.5%" },
  { key: "road", label: "Road Travel", to: "/road", left: "44.5%", width: "6.5%" },
  { key: "water", label: "Water Travel", to: "/water", left: "52.0%", width: "6.8%" },
  { key: "stay", label: "Stay", to: "/stays", left: "61.5%", width: "6%" },
];

export const AviationBanner = ({ testid = "page-header-aviation-panel" }) => (
  <div className="bg-[#eef7fc] border-b border-zinc-200" data-testid={testid}>
    <div className="relative max-w-[1983px] mx-auto">
      <img src={BRAND.innerPanel} alt="" aria-hidden="true"
        className="aviation-panel-enter block w-full h-auto object-contain" />
      {BANNER_HOTSPOTS.map((h) => (
        <RouterLink key={h.key} to={h.to} aria-label={`Open ${h.label}`} title={h.label}
          data-testid={`banner-link-${h.key}`}
          style={{ left: h.left, width: h.width, top: "80%", height: "18%" }}
          className="absolute rounded-sm hover:bg-blue-500/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary transition-colors">
          <span className="sr-only">{h.label}</span>
        </RouterLink>
      ))}
    </div>
  </div>
);

export const PageHeader = ({ eyebrow, title, subtitle, children, entityCode, plain = false }) => {
  if (plain) {
    return (
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 pb-6" data-testid="page-header">
        {eyebrow && <div className="text-xs font-mono uppercase tracking-widest text-primary mb-2">{eyebrow}</div>}
        <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight">{title}</h1>
        {subtitle && <p className="mt-3 text-muted-foreground max-w-2xl">{subtitle}</p>}
        {children}
      </div>
    );
  }

  const theme = vibrantTheme(entityCode || title);
  return (
    <header className="border-b border-zinc-300 bg-white" data-testid="page-header">
      <AviationBanner />
      <div className="text-white" style={{ backgroundColor: theme.bg, borderBottom: `4px solid ${theme.accent}` }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8 sm:py-10">
          {eyebrow && <div className="text-xs font-mono uppercase tracking-[0.18em] mb-2" style={{ color: theme.accent }}>{eyebrow}</div>}
          <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight" data-testid="page-header-title">{title}</h1>
          {subtitle && <p className="mt-3 text-white/90 max-w-3xl">{subtitle}</p>}
          {children}
        </div>
      </div>
    </header>
  );
};

export const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString());

export const TabBar = ({ tabs, active, onChange, attached = false }) => (
  <div className={attached ? "border-b border-zinc-300 bg-white overflow-x-auto" : "max-w-7xl mx-auto px-5 lg:px-8 mt-10 border-b border-zinc-300 overflow-x-auto"}
    data-testid={attached ? "entity-tabs-container" : "entity-tab-bar"}>
    <div className={attached ? "max-w-7xl mx-auto px-5 lg:px-8" : ""}>
      <div className="flex gap-1 min-w-max">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => onChange(t.key)} data-testid={`entity-tab-${t.key}`} aria-selected={active === t.key}
            className={`flex items-center gap-2 min-h-11 px-4 sm:px-5 py-3 text-sm font-700 -mb-px border-b-2 whitespace-nowrap transition-colors ${active === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>
    </div>
  </div>
);

import { Link as RouterLink } from "react-router-dom";
import { BRAND, codeGradient } from "../lib/assets";

export const IataBadge = ({ code, name, logo, className = "h-24", size = "text-3xl" }) => {
  const label = code || "—";
  const [g1, g2] = codeGradient(label);
  return (
    <div
      className={`relative w-full ${className} flex flex-col items-center justify-center text-center px-4 overflow-hidden`}
      style={{ backgroundImage: `linear-gradient(135deg, ${g1} 0%, ${g2} 100%)` }}
      data-testid={`iata-badge-${label}`}
    >
      {name && (
        <span className="font-heading font-900 text-white leading-tight text-lg sm:text-xl line-clamp-2 drop-shadow-[0_1px_4px_rgba(0,0,0,0.4)]">
          {name}
        </span>
      )}
      <span className={`font-mono font-700 text-white/95 tracking-wide ${name ? "text-base mt-1" : size + " font-900"} drop-shadow-[0_1px_4px_rgba(0,0,0,0.35)]`}>
        {label}
      </span>
      {logo && (
        <img
          src={logo}
          alt=""
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          className="absolute right-2.5 top-2.5 w-9 h-9 object-contain bg-white/95 rounded-md p-1 shadow-sm"
        />
      )}
    </div>
  );
};
